/**
 * QueueIndicator Component
 * 
 * Visual indicator showing the number of racks waiting in the activity queue.
 * Displays queue size, animated badge, and provides pause/resume controls.
 * 
 * Features:
 * - Badge with number of racks in queue
 * - Animated pulse effect when new items are added
 * - Click to pause/resume auto-switching
 * - Color-coded status (idle/active/paused)
 * - Tooltip with detailed queue information
 * 
 * @author BizClick WMS
 * @version 1.25 - Fase 3 Implementation
 */

import React, { useEffect, useState } from 'react';
import { Pause, Play, Clock, Activity, Info } from 'lucide-react';
import { QueueStats } from './types';
import { useTranslation } from '../../../../contexts/I18nProvider';

export interface QueueIndicatorProps {
  /** Current queue statistics */
  queueStats: QueueStats;
  
  /** Is auto-switching enabled */
  isAutoSwitching: boolean;
  
  /** Is carousel paused */
  isPaused: boolean;
  
  /** Callback when pause/resume is clicked */
  onTogglePause: () => void;
  
  /** Optional CSS class name */
  className?: string;
  
  /** Show detailed tooltip */
  showTooltip?: boolean;
}

/**
 * QueueIndicator Component
 */
export const QueueIndicator: React.FC<QueueIndicatorProps> = ({
  queueStats,
  isAutoSwitching,
  isPaused,
  onTogglePause,
  className = '',
  showTooltip = true
}) => {
  const { t } = useTranslation();
  const [isPulsing, setIsPulsing] = useState(false);
  const [prevQueueLength, setPrevQueueLength] = useState(0);
  const [showInfo, setShowInfo] = useState(false);

  /**
   * Trigger pulse animation when queue size increases
   */
  useEffect(() => {
    if (queueStats.queueLength > prevQueueLength) {
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), 1000);
    }
    setPrevQueueLength(queueStats.queueLength);
  }, [queueStats.queueLength]);

  /**
   * Get status color based on state
   */
  const getStatusColor = (): string => {
    if (isPaused) return 'bg-amber-500';
    if (!isAutoSwitching) return 'bg-gray-500';
    if (queueStats.queueLength === 0) return 'bg-blue-500';
    return 'bg-green-500';
  };

  /**
   * Get status text
   */
  const getStatusText = (): string => {
    if (isPaused) return t('manager.modules.rackBuilderLive.queueIndicator.statusPause', {}, 'Pause');
    if (!isAutoSwitching) return t('manager.modules.rackBuilderLive.queueIndicator.statusManual', {}, 'Manuell');
    if (queueStats.queueLength === 0) return t('manager.modules.rackBuilderLive.queueIndicator.statusWaiting', {}, 'Venter');
    return t('manager.modules.rackBuilderLive.queueIndicator.statusActive', {}, 'Aktiv');
  };

  /**
   * Format milliseconds to human-readable time
   */
  const formatAge = (milliseconds: number | undefined): string => {
    if (!milliseconds) return '0s';
    
    const seconds = Math.floor(milliseconds / 1000);
    if (seconds < 60) return `${seconds}s`;
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    
    const hours = Math.floor(minutes / 60);
    return `${hours}t ${minutes % 60}m`;
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Status Indicator */}
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
        <span className="text-sm font-medium text-gray-700">
          {getStatusText()}
        </span>
      </div>

      {/* Queue Badge */}
      <div className="relative">
        <div
          className={`
            flex items-center gap-2 px-4 py-2 rounded-lg
            bg-white border-2 border-gray-200
            transition-all duration-300
            ${isPulsing ? 'scale-110 border-blue-400 shadow-lg' : ''}
            ${queueStats.queueLength > 0 ? 'border-blue-300' : ''}
          `}
        >
          <Activity className={`w-5 h-5 ${
            queueStats.queueLength > 0 ? 'text-blue-600' : 'text-gray-400'
          }`} />
          
          <div className="flex flex-col">
            <span className={`text-2xl font-bold ${
              queueStats.queueLength > 0 ? 'text-blue-600' : 'text-gray-400'
            }`}>
              {queueStats.queueLength}
            </span>
            <span className="text-xs text-gray-500">
              {queueStats.queueLength === 1 ? t('manager.modules.rackBuilderLive.queueIndicator.rackSingular', {}, 'reol') : t('manager.modules.rackBuilderLive.queueIndicator.rackPlural', {}, 'reoler')}
            </span>
          </div>
        </div>

        {/* Pulse Animation Indicator */}
        {isPulsing && (
          <div className="absolute inset-0 rounded-lg border-2 border-blue-400 animate-ping" />
        )}
      </div>

      {/* Info Button & Tooltip */}
      {showTooltip && (
        <div className="relative">
          <button
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => setShowInfo(false)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <Info className="w-4 h-4 text-gray-500" />
          </button>

          {/* Tooltip */}
          {showInfo && queueStats.queueLength > 0 && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 z-50">
              <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">{t('manager.modules.rackBuilderLive.queueIndicator.totalEvents', {}, 'Totalt hendelser:')}</span>
                    <span className="font-medium">{queueStats.totalEvents}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">{t('manager.modules.rackBuilderLive.queueIndicator.avgPerRack', {}, 'Gjennomsnitt per reol:')}</span>
                    <span className="font-medium">
                      {queueStats.avgEventsPerRack.toFixed(1)}
                    </span>
                  </div>
                  {queueStats.oldestItemAge !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('manager.modules.rackBuilderLive.queueIndicator.oldestInQueue', {}, 'Eldste i kø:')}</span>
                      <span className="font-medium">
                        {formatAge(queueStats.oldestItemAge)}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Arrow */}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                  <div className="w-2 h-2 bg-gray-900 rotate-45" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pause/Resume Button */}
      {isAutoSwitching && (
        <button
          onClick={onTogglePause}
          className={`
            flex items-center gap-2 px-4 py-2 rounded-lg
            transition-all duration-200
            ${isPaused
              ? 'bg-green-500 hover:bg-green-600 text-white'
              : 'bg-amber-500 hover:bg-amber-600 text-white'
            }
            shadow-sm hover:shadow-md
          `}
          title={isPaused ? t('manager.modules.rackBuilderLive.queueIndicator.resumeAutoSwitch', {}, 'Fortsett automatisk bytte') : t('manager.modules.rackBuilderLive.queueIndicator.pauseAutoSwitch', {}, 'Pause automatisk bytte')}
        >
          {isPaused ? (
            <>
              <Play className="w-4 h-4" fill="currentColor" />
              <span className="text-sm font-medium">{t('manager.modules.rackBuilderLive.queueIndicator.resume', {}, 'Fortsett')}</span>
            </>
          ) : (
            <>
              <Pause className="w-4 h-4" fill="currentColor" />
              <span className="text-sm font-medium">{t('manager.modules.rackBuilderLive.queueIndicator.pause', {}, 'Pause')}</span>
            </>
          )}
        </button>
      )}

      {/* Dwell Time Indicator (when active) */}
      {!isPaused && isAutoSwitching && queueStats.queueLength > 0 && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-lg">
          <Clock className="w-4 h-4 text-blue-600" />
          <span className="text-xs font-medium text-blue-700">
            {t('manager.modules.rackBuilderLive.queueIndicator.switchingAutomatically', {}, 'Bytter automatisk')}
          </span>
        </div>
      )}
    </div>
  );
};

export default QueueIndicator;
